import _ from 'lodash';
import { createActions } from 'redux-actions';

import { createAlert } from './ActionCreators';
import ChannelAPIConnectors from '../api/ChannelAPIConnectors';
import createAsyncActionTypes from '../utils/createAsyncActionTypes';

import {
  ADMIN_ID,
  PATIENT_ID,
} from '../constants/RoleConstants';
import {
  CHANNEL,
  ACK_CHANNEL_READ,
  CREATE_CHANNEL,
  CREATE_CHANNEL_MEMBERS,
  CREATE_CHANNEL_MESSAGE,
  DELETE_CHANNEL,
  DELETE_CHANNEL_MEMBER,
  FETCH_CHANNEL,
  FETCH_CHANNEL_MEMBERS,
  FETCH_CHANNEL_MSGS,
  FETCH_PREV_CHANNEL_MSGS,
  FETCH_TAGGABLE_MEDICAL_RECORDS,
  GET_INVITABLE_USERS,
  SET_CURRENT_CHANNEL,
  UPDATE_CHANNEL,
  UPLOAD_CHANNEL_AVATAR,
} from '../constants/ActionTypes';

const plainActions = createActions(
  ACK_CHANNEL_READ,
  CREATE_CHANNEL_MEMBERS,
  CREATE_CHANNEL_MESSAGE,
  DELETE_CHANNEL,
  DELETE_CHANNEL_MEMBER,
  FETCH_PREV_CHANNEL_MSGS,
  SET_CURRENT_CHANNEL,
  UPDATE_CHANNEL,
  ...createAsyncActionTypes([
    CREATE_CHANNEL,
    DELETE_CHANNEL_MEMBER,
    FETCH_CHANNEL,
    FETCH_CHANNEL_MEMBERS,
    FETCH_CHANNEL_MSGS,
    FETCH_TAGGABLE_MEDICAL_RECORDS,
    GET_INVITABLE_USERS,
    UPLOAD_CHANNEL_AVATAR,
  ]),
  { prefix: CHANNEL },
);

const thunkActions = {
  fetchChannels: () => (dispatch, getState) => {
    const {
      fetchChannelRequest,
      fetchChannelSuccess,
      fetchChannelFailure,
    } = plainActions;

    if (getState().channel.isFetchingChannels) return;

    dispatch(fetchChannelRequest());
    ChannelAPIConnectors.getChannels().then((channels) => {
      dispatch(fetchChannelSuccess(channels));
    }).catch((err) => {
      dispatch(fetchChannelFailure());
      dispatch(createAlert(err));
    });
  },
  createChannel: data => (dispatch) => {
    const { createChannelSuccess } = plainActions;
    ChannelAPIConnectors.createChannel(data).then((channel) => {
      // NOTE: "unReadCnt" is crafted by the backend, instead of property of
      // channel model, so we need to add it here.
      dispatch(createChannelSuccess({ ...channel, unReadCnt: 1 }));
    }).catch(err => dispatch(createAlert(err)));
  },
  ackChannel: channelId => (dispatch) => {
    const { ackChannelRead } = plainActions;
    ChannelAPIConnectors.ackChannelRead(channelId).then((updatedChUserInfo) => {
      dispatch(ackChannelRead(updatedChUserInfo));
    }).catch(err => dispatch(createAlert(err)));
  },
  fetchChannelMembers: channelId => (dispatch, getState) => {
    const {
      fetchChannelMembersRequest,
      fetchChannelMembersSuccess,
      fetchChannelMembersFailure,
    } = plainActions;

    if (getState().channel.isFetchingChannelMembers) return;

    dispatch(fetchChannelMembersRequest());
    ChannelAPIConnectors.getChannelMembers(channelId).then((members) => {
      dispatch(fetchChannelMembersSuccess({ channelId, members }));
    }).catch((err) => {
      dispatch(fetchChannelMembersFailure());
      dispatch(createAlert(err));
    });
  },
  modifyChannelTitle: (channelId, title) => (dispatch) => {
    const { updateChannel } = plainActions;

    ChannelAPIConnectors.updateChannelTitle(channelId, title).then(() => {
      dispatch(updateChannel({ channelId, displayName: title }));
    }).catch(err => dispatch(createAlert(err)));
  },
  fetchPrevChannelMessages: (channelId, fromMsgIdx, limit = 30) => (dispatch) => {
    const { fetchPrevChannelMsgs } = plainActions;

    ChannelAPIConnectors.getPrevChannelMessages(channelId, fromMsgIdx, limit).then((msgs) => {
      dispatch(fetchPrevChannelMsgs({ channelId, msgs }));
    }).catch(err => dispatch(createAlert(err)));
  },
  fetchTaggableMedicalRecords: channelId => async (dispatch, getState) => {
    const {
      fetchTaggableMedicalRecordsRequest,
      fetchTaggableMedicalRecordsSuccess,
      fetchTaggableMedicalRecordsFailure,
    } = plainActions;
    const channelMembers = getState().channel.membersInChannel[channelId];
    dispatch(fetchTaggableMedicalRecordsRequest());

    // NOTE: id = 0 indicate 'No Patient'.
    const patientUser =
      _.find(channelMembers, { user: { roleId: PATIENT_ID } }) || { user: { id: 0 } };

    try {
      const medicalRecords =
        await ChannelAPIConnectors.fetchTaggableMedicalRecords(patientUser.user.id);
      dispatch(fetchTaggableMedicalRecordsSuccess(medicalRecords));
    } catch (err) {
      dispatch(fetchTaggableMedicalRecordsFailure());
      dispatch(createAlert(err));
    }
  },
  // NOTE:
  // Because we need to check the user's startMsgIdx with channel's totalMsgCnt,
  // we should make sure we have channelMember's and channel's information so
  // create this api to fetch members first and then fetch messages.
  fetchAllChannelDetailInformation: channelId => async (dispatch, getState) => {
    const {
      fetchChannelMembersRequest,
      fetchChannelMembersSuccess,
      fetchChannelMembersFailure,
      fetchChannelMsgsFailure,
      fetchChannelMsgsSuccess,
      fetchChannelMsgsRequest,
    } = plainActions;
    const { userId } = getState().user;
    const { channels } = getState().channel;
    const currentUserId = parseInt(userId, 10);
    const channelIdx = _.findIndex(channels, { id: channelId });
    const currentChannel = channels[channelIdx];

    dispatch(fetchChannelMembersRequest());
    dispatch(fetchChannelMsgsRequest());
    try {
      const members = await ChannelAPIConnectors.getChannelMembers(channelId);
      dispatch(fetchChannelMembersSuccess({ channelId, members }));

      // NOTE: leverage the latest state shape
      const currentUserInfo = getState().channel.membersInChannel[channelId][currentUserId];

      // NOTE: we don't need to fetch messsage when this user is too new.
      let msgs = [];
      if (currentUserInfo.startMsgIdx <= currentChannel.totalMsgCnt) {
        msgs = await ChannelAPIConnectors.getChannelMessages(channelId);
      }
      dispatch(fetchChannelMsgsSuccess({ channelId, msgs }));

      if (getState().user.profile.roleId !== ADMIN_ID) {
        await thunkActions.fetchTaggableMedicalRecords(channelId)(dispatch, getState);
      }
    } catch (err) {
      dispatch(fetchChannelMembersFailure());
      dispatch(fetchChannelMsgsFailure());
      dispatch(createAlert(err));
    }
  },
  createChannelTextMessage: (channelId, text) => (dispatch) => {
    const { createChannelMessage } = plainActions;

    ChannelAPIConnectors.createChannelTextMessage(channelId, text).then((msg) => {
      dispatch(createChannelMessage({ channelId, msg }));
    }).catch(err => dispatch(createAlert(err)));
  },
  uploadChannelAvatar: (channelId, formData) => async (dispatch) => {
    const {
      uploadChannelAvatarRequest,
      uploadChannelAvatarSuccess,
      uploadChannelAvatarFailure,
    } = plainActions;

    dispatch(uploadChannelAvatarRequest());
    try {
      const avatar = await ChannelAPIConnectors.uploadChannelAvatar(channelId, formData);
      dispatch(uploadChannelAvatarSuccess({ channelId, avatar }));
    } catch (err) {
      // We throw error here to let the InputFile component to handle it.
      dispatch(uploadChannelAvatarFailure());
      throw err;
    }
  },
  getInvitableUsers: channelId => async (dispatch, getState) => {
    const {
      getInvitableUsersRequest,
      getInvitableUsersSuccess,
      getInvitableUsersFailure,
    } = plainActions;
    const roleId = _.get(getState(), ['user', 'profile', 'roleId']);

    if (!roleId || roleId === PATIENT_ID) return;

    try {
      dispatch(getInvitableUsersRequest());
      const users = await ChannelAPIConnectors.getInvitableMembers(channelId);
      dispatch(getInvitableUsersSuccess(users));
    } catch (err) {
      dispatch(getInvitableUsersFailure());
      dispatch(createAlert(err));
    }
  },
  deleteChannelMemberById: (channelId, memberId) => async (dispatch) => {
    const {
      deleteChannelMemberRequest,
      deleteChannelMemberSuccess,
      deleteChannelMemberFailure,
    } = plainActions;

    try {
      dispatch(deleteChannelMemberRequest());
      await ChannelAPIConnectors.deleteChannelMember(channelId, memberId);
      dispatch(deleteChannelMemberSuccess());
      dispatch(thunkActions.getInvitableUsers(channelId));
    } catch (err) {
      dispatch(deleteChannelMemberFailure());
      dispatch(createAlert(err));
    }
  },
};

export default {
  ...plainActions,
  ...thunkActions,
};




// WEBPACK FOOTER //
// ./src/actions/ChannelActionCreators.js